import { useEffect, useRef, useState } from "react";
import SpotifyCTA from "./SpotifyCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TranslationReader({ code, name, flag, onClose }) {
  const [chapters, setChapters] = useState([]);
  const [active, setActive] = useState(null);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [hasAudio, setHasAudio] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/book/translations`)
      .then((r) => r.json())
      .then((d) => {
        const t = (d.translations || []).find((x) => x.code === code);
        if (!t) { setErr("Translation not available yet"); return; }
        setChapters(t.chapters || []);
        if (t.chapters?.length) setActive(t.chapters[0].index);
      })
      .catch((e) => setErr(e.message || "Failed"));
  }, [code]);

  useEffect(() => {
    if (active == null) return;
    let cancelled = false;
    setLoading(true);
    // Pause any playing audio when changing chapter
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const url = `${API}/book/translation/${code}/chapter/${active}`;
    (async () => {
      try {
        const r = await fetch(url);
        const d = await r.json();
        if (cancelled) return;
        setBody(d.text || "");
        setTitle(d.title || "");
        setHasAudio(Boolean(d.has_audio));
        setErr("");
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, active]);

  const audioUrl = active != null && hasAudio
    ? `${API}/book/translation/${code}/chapter/${active}/audio`
    : null;

  return (
    <div className="trd-modal" data-testid="translation-reader-modal" role="dialog" aria-modal="true">
      <div className="trd-shell">
        <header className="trd-header">
          <div className="trd-title">
            <span className="trd-flag" aria-hidden="true">{flag || "🌐"}</span>
            <div>
              <div className="trd-h2">Twice Built, Twice Broken</div>
              <div className="trd-sub">{name} edition · {chapters.length} chapters</div>
            </div>
          </div>
          <button
            type="button"
            className="trd-close"
            onClick={onClose}
            data-testid="translation-reader-close"
            aria-label="Close"
          >✕</button>
        </header>
        <div className="trd-body">
          <aside className="trd-side" data-testid="translation-chapter-list">
            <ul>
              {chapters.map((c) => (
                <li key={c.index}>
                  <button
                    type="button"
                    className={"trd-chip" + (c.index === active ? " trd-chip-active" : "")}
                    onClick={() => setActive(c.index)}
                    data-testid={`translation-chapter-${c.index}`}
                  >
                    <span className="trd-chip-num">{String(c.index).padStart(2, "0")}</span>
                    <span className="trd-chip-title">{c.title}</span>
                    {c.has_audio && (
                      <span className="trd-chip-audio" aria-label="audio available" title="Audio available">♪</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <article className="trd-main" data-testid="translation-chapter-body">
            {title && <h3 className="trd-chapter-title">{title}</h3>}
            <SpotifyCTA code={code} native={name} compact />
            {audioUrl && (
              <div className="trd-audio" data-testid="translation-audio-bar">
                <span className="trd-audio-label">🎧 Sesli oku</span>
                <audio
                  ref={audioRef}
                  controls
                  preload="metadata"
                  src={audioUrl}
                  data-testid="translation-audio-player"
                >
                  Audio playback not supported.
                </audio>
              </div>
            )}
            {!audioUrl && hasAudio === false && !loading && (
              <div className="trd-audio-soon" data-testid="translation-audio-soon">
                🎧 Audio for this chapter is being produced — coming soon.
              </div>
            )}
            {err && <div className="trd-err">{err}</div>}
            {loading && <div className="trd-muted">Yükleniyor…</div>}
            {!loading && body.split(/\n{2,}/).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </article>
        </div>
      </div>
    </div>
  );
}
